import { useState } from 'react';
import useTemplates from 'src/data-capture/hooks/templates/useTemplates';
import type { CalendarEvent } from 'src/meetings/models';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { useCalendarEventMeetingNote } from './useCalendarEventMeetingNote';
import { useGenerateCalendarEventMeetingAgenda } from './useGenerateCalendarEventMeetingAgenda';
import { useGenerateCalendarEventMeetingNote } from './useGenerateCalendarEventMeetingNote';
import { useRegenerateTranscriptSummary } from './useRegenerateTranscriptSummary';

export type RegeneratableArtifact = 'summary' | 'meetingNote' | 'meetingAgenda';

export const ARTIFACT_LABELS: Record<RegeneratableArtifact, string> = {
  summary: 'transcript summary',
  meetingNote: 'meeting note',
  meetingAgenda: 'meeting agenda',
};

// Offer to regenerate only outputs that already exist, since those are the ones a
// participant change could have left stale.
export function getRegeneratableArtifacts(event: CalendarEvent): RegeneratableArtifact[] {
  const artifacts: RegeneratableArtifact[] = [];
  if (event.hasTranscription) artifacts.push('summary');
  if (event.hasMeetingNote) artifacts.push('meetingNote');
  if (event.hasMeetingAgenda) artifacts.push('meetingAgenda');
  return artifacts;
}

const defaultTemplateId = (templates: { id: string; isDefault: boolean }[]): string | undefined =>
  (templates.find(template => template.isDefault) ?? templates[0])?.id;

type UseRegenerateMeetingArtifactsResult = {
  artifacts: RegeneratableArtifact[];
  regenerate: () => Promise<void>;
  isRegenerating: boolean;
};

// Orchestrates re-running the transcript summary, meeting note and meeting agenda
// after a meeting's participants change, reusing the existing generation endpoints.
export const useRegenerateMeetingArtifacts = (
  event: CalendarEvent | null | undefined,
): UseRegenerateMeetingArtifactsResult => {
  const [isRegenerating, setIsRegenerating] = useState(false);

  const eventId = event?.id ?? '';
  const artifacts = event ? getRegeneratableArtifacts(event) : [];

  const { regenerateSummary } = useRegenerateTranscriptSummary(eventId);
  const { generateMeetingNote } = useGenerateCalendarEventMeetingNote(eventId);
  const { generateMeetingAgenda } = useGenerateCalendarEventMeetingAgenda(eventId);

  // The note keeps the template it was built from; fall back to the default so a
  // note first created from an agenda file can still be regenerated.
  const { meetingNote } = useCalendarEventMeetingNote(eventId, {
    enabled: !!event?.hasMeetingNote,
  });
  const { files: noteTemplates } = useTemplates({ templateType: 'MEETING_NOTE' });
  const { files: agendaTemplates } = useTemplates({ templateType: 'MEETING_AGENDA' });

  const regenerateNote = async () => {
    const templateId = meetingNote?.templateId ?? defaultTemplateId(noteTemplates);
    if (!templateId) {
      notifyAndLogError(
        'Could not regenerate the meeting note',
        'No meeting note template is available.',
        new Error('No meeting note template available for regeneration'),
        { eventId },
      );
      return;
    }
    try {
      await generateMeetingNote({ templateId, summarise: true });
    } catch (error) {
      notifyAndLogError('Could not regenerate the meeting note', 'Please try again later.', error, {
        eventId,
      });
    }
  };

  const regenerateAgenda = async () => {
    // The stored agenda doesn't keep the template it was built from, so we can only
    // fall back to the current default agenda template here.
    const templateId = defaultTemplateId(agendaTemplates);
    if (!templateId) {
      notifyAndLogError(
        'Could not regenerate the meeting agenda',
        'No meeting agenda template is available.',
        new Error('No meeting agenda template available for regeneration'),
        { eventId },
      );
      return;
    }
    try {
      await generateMeetingAgenda({ templateId });
    } catch (error) {
      notifyAndLogError(
        'Could not regenerate the meeting agenda',
        'Please try again later.',
        error,
        { eventId },
      );
    }
  };

  // Each task handles its own errors and allSettled never rejects, so no try/finally
  // is needed to reset the flag - and try/finally is unsupported by the React Compiler.
  const regenerate = async () => {
    setIsRegenerating(true);
    // The summary endpoint blocks until the LLM finishes (up to a minute); fire it and
    // let the meeting's transcript polling surface progress rather than holding the
    // prompt open. The note and agenda calls only enqueue background jobs, so await
    // them to guarantee they are dispatched before the prompt closes.
    if (artifacts.includes('summary')) void regenerateSummary();
    const tasks: Promise<void>[] = [];
    if (artifacts.includes('meetingNote')) tasks.push(regenerateNote());
    if (artifacts.includes('meetingAgenda')) tasks.push(regenerateAgenda());
    await Promise.allSettled(tasks);
    setIsRegenerating(false);
  };

  return { artifacts, regenerate, isRegenerating };
};
